import { Route } from '@angular/router';
import { LayoutComponent } from './core/components/layout/layout.component';
import { ClaimGuard } from './core/guards/claim.guard.ts';
import { SendErrorMessageemployeeComponent } from './core/components/send-error-message/send-error-message.component';
import { NotFoundComponent } from '@awo-care/shared-ui';
import { FEATURE_FLAGS } from './core/feature-flags';
import {
  APP_LEGACY_REDIRECTS,
  TEAMLEAD_MAIL_LEGACY_REDIRECT,
} from './core/navigation/navigation.config';

// Hinweis: Die Top-Level-Pfade hier müssen mit der Locale-Redirect-Allowlist in
// firebase.json (hosting target "employee-ui" -> "redirects") synchron bleiben.
// Diese leitet locale-freie Deep-Links (z.B. aus E-Mails) auf /de/... um.
export const appRoutes: Route[] = [
  {
    path: '',
    component: LayoutComponent,
    canActivate: [ClaimGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

      {
        path: 'overview',
        loadChildren: () =>
          import('./overview/overview.routes').then((m) => m.OVERVIEW_ROUTES),
        data: { requiredClaims: ['employee'] },
      },

      {
        path: 'dashboard',
        loadChildren: () =>
          import('./studentDashboard/student-dashboard.routes').then(
            (mod) => mod.unifiedDashboardRoutes
          ),
        data: { title: 'Schüler' },
      },

      {
        path: 'schools',
        loadChildren: () =>
          import('./schools/schools.routes').then((mod) => mod.SCHOOLS_ROUTES),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['manager'], title: 'Schulen' },
      },
      {
        path: 'duplicates',
        loadComponent: () =>
          import('./duplicates/duplicates.component').then(
            (m) => m.DuplicatesComponent
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['manager'], title: 'Doubletten' },
      },
      {
        path: 'holiday-care-groups',
        loadChildren: () =>
          import('./holiday-care-groups/holiday-care-groups.routes').then(
            (m) => m.HOLIDAY_CARE_GROUPS_ROUTES
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['manager'], title: 'Schulverbünde' },
      },
      {
        path: 'holiday-care',
        loadChildren: () =>
          import(
            './holiday-care-enrollments/holiday-care-enrollments.routes'
          ).then((mod) => mod.HOLIDAY_CARE_ENROLLMENTS_ROUTES),
        canActivate: [ClaimGuard],
        // Teamleitung liest nur die Anmeldungen ihrer Schulen (Stammschule, #1271).
        data: { requiredClaims: ['employee'], title: 'Ferienbetreuung' },
      },
      {
        path: 'alterations',
        loadChildren: () =>
          import('./alterations/alterations.routes').then(
            (mod) => mod.alterationsRoutes
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['admin', 'manager'], title: 'Vertragsänderungen' },
      },
      {
        path: 'export',
        loadChildren: () =>
          import('./export/export.routes').then((mod) => mod.EXPORT_ROUTES),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['manager'], title: 'Export' },
      },
      {
        path: 'tickets',
        loadChildren: () =>
          import('./tickets/tickets.routes').then((mod) => mod.TICKET_ROUTES),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['employee'], title: 'Tickets' },
      },
      {
        path: 'parent-letters',
        loadChildren: () =>
          import('./parent-letters/parent-letters.routes').then(
            (mod) => mod.PARENT_LETTERS_ROUTES
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['admin'], title: 'Elternschreiben' },
      },
      // „Versand an Teamleitungen“ ist Reiter von „Elternschreiben“ (#1397); der alte Pfad
      // leitet um. Bis zur Freigabe (#1282) ausgeblendet: Flag in core/feature-flags.ts.
      FEATURE_FLAGS.teamleadMailEnabled
        ? {
            path: TEAMLEAD_MAIL_LEGACY_REDIRECT.from,
            redirectTo: TEAMLEAD_MAIL_LEGACY_REDIRECT.to,
            pathMatch: 'prefix',
          }
        : { path: TEAMLEAD_MAIL_LEGACY_REDIRECT.from, redirectTo: 'dashboard', pathMatch: 'prefix' },
      {
        path: 'parent-notices',
        loadChildren: () =>
          import('./parent-notices/parent-notices.routes').then(
            (mod) => mod.PARENT_NOTICES_ROUTES
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['admin'], title: 'Eltern-Hinweise' },
      },
      {
        path: 'legal',
        loadChildren: () => import('./legal/legal.routes').then((mod) => mod.LEGAL_ROUTES),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['admin'], title: 'Rechtstexte' },
      },
      ...APP_LEGACY_REDIRECTS.map(({ from, to }) => ({
        path: from,
        redirectTo: to,
        pathMatch: 'full' as const,
      })),
      {
        path: 'manuals',
        loadChildren: () =>
          import('./manuals/manuals.routes').then(
            (mod) => mod.MANUALS_ROUTES
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['employee'], title: 'Hilfe & Handbücher' },
      },
      {
        path: 'knowledgebase',
        loadChildren: () =>
          import('./knowledgebase/knowledgebase.routes').then(
            (mod) => mod.KNOWLEDGEBASE_ROUTES
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['manager'], title: 'Wissensdatenbank' },
      },
      {
        path: 'employees',
        loadChildren: () =>
          import('./userManagement/user-management.routes').then(
            (mod) => mod.User_MANAGEMENT_ROUTES
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['manager'], title: 'Mitarbeiter' },
      },
      {
        path: 'users',
        loadChildren: () =>
          import('./user/user.routes').then((mod) => mod.USER_ROUTES),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['manager'], title: 'Benutzer' },
      },
      {
        path: 'admin/users',
        redirectTo: 'users',
        pathMatch: 'prefix',
      },
      { path: 'roles', redirectTo: 'admin/roles', pathMatch: 'full' },
      {
        path: 'settings',
        loadChildren: () =>
          import('./settings/settings.routes').then(
            (mod) => mod.SETTINGS_ROUTES
          ),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['employee'], title: 'Profil' },
      },
      {
        path: 'sendErrorMessage',
        component: SendErrorMessageemployeeComponent,
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./admin/admin.routes').then((mod) => mod.ADMIN_ROUTES),
        canActivate: [ClaimGuard],
        data: { requiredClaims: ['admin'] },
      },
    ],
  },

  {
    path: 'auth',
    loadChildren: () =>
      import('./auth/auth.routes').then((mod) => mod.AUTH_ROUTES),
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];
